/**
 * React Query hooks for Author with AI. The build view query holds the whole
 * transcript: the first load follows the message cursor forward until it is
 * exhausted, and every poll asks only for messages after the highest loaded
 * sequence. Admission responses (which carry the updated build) are written
 * into the same cache entry, so a sent message or action shows immediately.
 */

import { useEffect, useRef } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  AuthorWithAiApiError,
  createBuild,
  fetchAuthorWithAiDiagnostics,
  fetchBuildList,
  fetchBuildQuestionFacets,
  fetchBuildQuestions,
  type BuildQuestionFilters,
  fetchBuildView,
  postBuildAction,
  postBuildMessage,
  type AdmissionResponse,
  type BuildAction,
  type BuildList,
  type BuildStatus,
  type BuildView,
  type CreateBuildInput,
  type MessageView,
} from './authorWithAiApi';

export const ACTIVE_POLL_MS = 2000;
export const LIST_PAGE_SIZE = 4;

export const authorWithAiKeys = {
  all: ['author-with-ai'] as const,
  diagnostics: () => [...authorWithAiKeys.all, 'diagnostics'] as const,
  lists: () => [...authorWithAiKeys.all, 'list'] as const,
  list: (accountId: number | null, statuses: readonly BuildStatus[]) =>
    [...authorWithAiKeys.lists(), accountId, [...statuses]] as const,
  questions: (buildId: string) => [...authorWithAiKeys.all, 'build', buildId, 'questions'] as const,
  questionFacets: (buildId: string) => [...authorWithAiKeys.all, 'build', buildId, 'question-facets'] as const,
  build: (buildId: string) => [...authorWithAiKeys.all, 'build', buildId] as const,
};

const isTransient = (error: unknown): boolean => !(error instanceof AuthorWithAiApiError) || error.transient;

/** Queries retry transport failures only; a 404 or 409 is an answer, not a glitch. */
const retryTransient = (failureCount: number, error: unknown): boolean => failureCount < 2 && isTransient(error);

// ── Availability ─────────────────────────────────────────────────────────────

export type AuthorWithAiAvailability =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'available'; ready: boolean; missingSettings: string[]; uploadsReady: boolean };

export function useAuthorWithAiAvailability(): AuthorWithAiAvailability {
  const query = useQuery({
    queryKey: authorWithAiKeys.diagnostics(),
    queryFn: fetchAuthorWithAiDiagnostics,
    staleTime: 5 * 60 * 1000,
    retry: retryTransient,
    placeholderData: undefined,
  });
  if (query.isPending) return { status: 'loading' };
  const diagnostics = query.data;
  if (query.isError || !diagnostics || !diagnostics.enabled) return { status: 'unavailable' };
  const missingSettings = diagnostics.components
    .filter((component) => component.required && !component.ok)
    .flatMap((component) => component.missing);
  return { status: 'available', ready: diagnostics.ready, missingSettings, uploadsReady: diagnostics.uploads?.ready ?? true };
}

// ── Lists ────────────────────────────────────────────────────────────────────

export function useBuildList(accountId: number | null, statuses: readonly BuildStatus[] = [], options: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: authorWithAiKeys.list(accountId, statuses),
    queryFn: ({ pageParam }) => fetchBuildList({ accountId, statuses, cursor: pageParam, limit: LIST_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: BuildList) => lastPage.nextCursor,
    enabled: options.enabled ?? true,
    staleTime: 0,
    retry: retryTransient,
    // A list from another content context must never show while this one loads.
    placeholderData: undefined,
    refetchInterval: (current) => {
      const pages = current.state.data?.pages ?? [];
      return pages.some((page) => page.items.some((item) => item.status === 'building')) ? ACTIVE_POLL_MS : false;
    },
  });

  // Only lists showing a building item poll. When one stops building, every
  // list refetches, so a finished build reaches Recent without a reload.
  const buildingIds = (query.data?.pages ?? [])
    .flatMap((page) => page.items)
    .filter((item) => item.status === 'building')
    .map((item) => item.id)
    .join(',');
  const previousBuilding = useRef<string[]>([]);
  useEffect(() => {
    const current = buildingIds ? buildingIds.split(',') : [];
    if (previousBuilding.current.some((id) => !current.includes(id))) {
      void queryClient.invalidateQueries({ queryKey: authorWithAiKeys.lists() });
    }
    previousBuilding.current = current;
  }, [buildingIds, queryClient]);

  return query;
}

// ── Build view ───────────────────────────────────────────────────────────────

const highestSequence = (messages: readonly MessageView[]): number =>
  messages.reduce((highest, message) => Math.max(highest, message.sequence), 0);

/** Appends messages by sequence: none omitted, none duplicated, always in order. */
export function mergeMessages(existing: readonly MessageView[], incoming: readonly MessageView[]): MessageView[] {
  const bySequence = new Map(existing.map((message) => [message.sequence, message]));
  for (const message of incoming) bySequence.set(message.sequence, message);
  return [...bySequence.values()].sort((a, b) => a.sequence - b.sequence);
}

/** Loads every message after `after`, following the cursor; the last page carries the current view. */
async function loadForward(buildId: string, after: number): Promise<BuildView> {
  let page = await fetchBuildView(buildId, { messagesAfter: after });
  let messages = page.messages;
  while (page.nextMessageCursor) {
    page = await fetchBuildView(buildId, { messagesAfter: Number(page.nextMessageCursor) });
    messages = mergeMessages(messages, page.messages);
  }
  return { ...page, messages, nextMessageCursor: null };
}

export function isBuildActive(view: BuildView | undefined): boolean {
  return Boolean(view && (view.execution.active || view.status === 'building'));
}

export function useBuildView(buildId: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: authorWithAiKeys.build(buildId),
    queryFn: async () => {
      const cached = queryClient.getQueryData<BuildView>(authorWithAiKeys.build(buildId));
      if (!cached) return loadForward(buildId, 0);
      const latest = await loadForward(buildId, highestSequence(cached.messages));
      return { ...latest, messages: mergeMessages(cached.messages, latest.messages) };
    },
    staleTime: 0,
    retry: retryTransient,
    placeholderData: undefined,
    refetchInterval: (query) => (isBuildActive(query.state.data) ? ACTIVE_POLL_MS : false),
  });
}

/**
 * Writes the build view from an admission response into the cache (server
 * truth, no optimistic echo). A read already in flight started before the
 * admission and would overwrite it with an idle view, so it is cancelled first.
 */
export async function writeAdmittedBuild(queryClient: QueryClient, response: AdmissionResponse): Promise<void> {
  const build = response.build;
  if (!build) return;
  await queryClient.cancelQueries({ queryKey: authorWithAiKeys.build(build.id) });
  queryClient.setQueryData<BuildView>(authorWithAiKeys.build(build.id), (cached) => ({
    ...build,
    messages: mergeMessages(cached?.messages ?? [], build.messages),
    nextMessageCursor: null,
  }));
  void queryClient.invalidateQueries({ queryKey: authorWithAiKeys.lists() });
}

/** After busy or invalid_state the local view is behind the server: refetch it. */
function refreshOnConflict(queryClient: QueryClient, buildId: string, error: unknown): void {
  if (error instanceof AuthorWithAiApiError && (error.code === 'busy' || error.code === 'invalid_state')) {
    void queryClient.invalidateQueries({ queryKey: authorWithAiKeys.build(buildId) });
  }
}

// ── Commands ─────────────────────────────────────────────────────────────────

export function useCreateBuild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBuildInput) => createBuild(input),
    retry: false,
    onSuccess: (response) => writeAdmittedBuild(queryClient, response),
  });
}

export function usePostMessage(buildId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => postBuildMessage(buildId, text),
    retry: false,
    onSuccess: (response) => writeAdmittedBuild(queryClient, response),
    onError: (error) => refreshOnConflict(queryClient, buildId, error),
  });
}

export function usePostAction(buildId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: BuildAction) => postBuildAction(buildId, action),
    retry: false,
    onSuccess: (response) => writeAdmittedBuild(queryClient, response),
    onError: (error) => refreshOnConflict(queryClient, buildId, error),
  });
}

/** One page of the build's bank for the Question Bank picker; the previous page stays visible while the next loads. */
export function useBuildQuestionPage(buildId: string, params: BuildQuestionFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...authorWithAiKeys.questions(buildId), 'page', params],
    queryFn: () => fetchBuildQuestions(buildId, params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    enabled: options.enabled ?? true,
    retry: retryTransient,
  });
}

/** The bank's facets (concepts with live counts, taxonomy tree, type and purpose pairs), kept as long as the server's own cache (a minute). */
export function useBuildQuestionFacets(buildId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: authorWithAiKeys.questionFacets(buildId),
    queryFn: () => fetchBuildQuestionFacets(buildId),
    staleTime: 60 * 1000,
    enabled: options.enabled ?? true,
    retry: retryTransient,
  });
}

/** Combined permitted banks, paged and counted by the build-scoped endpoint. */
export function useInfiniteBuildQuestions(
  buildId: string,
  params: Omit<BuildQuestionFilters, 'page'>,
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    queryKey: [...authorWithAiKeys.questions(buildId), 'infinite', params],
    queryFn: ({ pageParam }) => fetchBuildQuestions(buildId, { ...params, page: pageParam, pageSize: params.pageSize ?? 25 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((total, page) => total + page.items.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    staleTime: 30 * 1000,
    enabled: options.enabled ?? true,
    retry: retryTransient,
  });
}
